import { getConfig } from '@/lib/get-config';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import { useQueries, useQuery } from '@tanstack/react-query';
import { get } from '../api/get';
import { getList } from '../api/get-list';
import { NESTED_KEY_SEPARATOR } from '../constants';
import type { useGetGenericInstanceProps } from '../types';
import { collectNestedIdsSingleInstance, filterNested } from './utils';
import { useMemo } from 'react';

/**
 * Props for the `useGetInstance` hook.
 */
interface UseGetInstanceProps<TInstance extends BaseInstance>
  extends useGetGenericInstanceProps<TInstance> {
  /** The name of the model to fetch. */
  modelName: ModelName;
}

/**
 * Custom React hook to fetch a single instance of a model along with its nested related data.
 *
 * @template TInstance - The type of the instance extending `BaseInstance`.
 *
 * @param {UseGetInstanceProps<TInstance>} options - Configuration options for fetching the instance.
 * @param {ModelName} options.modelName - The name of the model to fetch.
 * @param {string} options.id - The unique identifier of the instance to fetch.
 * @param {string[]} [options.nested=[]] - An array of nested field names to fetch related data.
 * @param {string} [options.overrideUrl] - An optional URL to override the default API endpoint.
 * @param {...any} [queryOptions] - Additional options to pass to the underlying React Query hooks.
 *
 * @returns {UseQueryResult<TInstance, Error>} - The result of the query including data, loading states, and error information.
 *
 * @throws {Error} If a specified nested field is not a relation field.
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError, error } = useGetInstance({
 *   modelName: 'User',
 *   id: 'user123',
 *   nested: ['posts', 'comments'],
 * });
 * ```
 */
export const useGetInstance = <TInstance extends BaseInstance>(
  options: UseGetInstanceProps<TInstance> & {
    modelName: ModelName;
  }
) => {
  const {
    modelName,
    id,
    overrideUrl,
    nested = [],
    enabled,
    waitForAllNested,
    ...queryOptions
  } = options;

  // Retrieve configuration for the specified model
  const config = getConfig<ModelName, TInstance>(modelName, true);

  /**
   * Filters out nested fields that should never be fetched.
   *
   * @returns {Array<keyof TInstance>} - Filtered array of nested field names.
   */
  const nestedKey = nested.join(NESTED_KEY_SEPARATOR);
  const filteredNested = useMemo(
    () => filterNested<TInstance>(nestedKey),
    [nestedKey]
  );

  /**
   * Fetches the main instance data using React Query's `useQuery`.
   *
   * The query is enabled only if an `id` is provided.
   */
  const mainQuery = useQuery({
    queryKey: [modelName, id, overrideUrl],
    queryFn: async () =>
      await get({
        modelName,
        id,
        overrideUrl,
      }),
    enabled: enabled !== false && Boolean(id),
    ...queryOptions,
  });

  /**
   * Collects IDs from the specified nested fields in the main instance data.
   */
  const nestedIds = collectNestedIdsSingleInstance(
    mainQuery.data,
    filteredNested
  );
  /**
   * Determines which nested fields need to be fetched based on the collected IDs and configuration.
   *
   * @returns {Array<{ fieldName: keyof TInstance; ids: string[]; relationModelName: ModelName; type: string }>}
   * An array of objects containing information about each nested field to fetch.
   *
   * @throws {Error} If a nested field is not a relation field.
   */
  const nestedToFetch =
    enabled === false
      ? []
      : (filteredNested
          .map((fieldName) => {
            const fieldConfig = config?.fields[fieldName];
            if (
              (fieldConfig && !('relationConfig' in fieldConfig)) ||
              !fieldConfig
            ) {
              throw new Error(
                `Field ${String(fieldName)} is not a relation field in model ${modelName}`
              );
            }
            const relationModelName = fieldConfig.relationConfig;
            const ids = nestedIds?.get(fieldName) ?? [];
            return {
              fieldName,
              ids,
              relationModelName,
              type: fieldConfig.type,
            };
          })
          .filter(({ ids }) => ids.length > 0) ?? []);

  /**
   * Uses React Query's `useQueries` to fetch all nested related data in parallel.
   */
  const nestedQueries = useQueries({
    queries: nestedToFetch.map(
      ({ fieldName, ids, relationModelName, type }) => ({
        queryKey: ['nested', fieldName, relationModelName, ids.join(',')],
        queryFn: async () => {
          const data = await getList<TInstance>({
            modelName: relationModelName,
            queryParams: {
              filters: {
                id: {
                  __in: ids.join(','),
                },
              },
              pageSize: ids.length,
            },
          });
          return {
            data: data.list,
            fieldName,
            type,
          };
        },
        enabled: enabled !== false && ids.length > 0,
      })
    ),
    // Allows us to get a stable aggregated return value instead of useQueries
    // default return value which is not stable.
    //! Key to not causing render loops!
    combine(result) {
      const data = result
        .map((res) => res.data)
        .filter((value) => value !== undefined);

      const isLoading = result.some((q) => q.isLoading);
      const isFetching = result.some((q) => q.isFetching);
      const isPending = result.some((q) => q.isPending);
      const isSuccess = result.every((q) => q.isSuccess);
      const isError = result.some((q) => q.isError);
      const isRefetching = result.some((q) => q.isRefetching);
      const isStale = result.every((q) => q.isStale);

      return {
        data,
        isPending,
        isSuccess,
        isFetching,
        isLoading,
        isError,
        isRefetching,
        isStale,
      };
    },
  });

  /**
   * Merges the fetched nested data with the main instance data. We no longer
   * mutate the main instance data since that won't cause updates when in
   * dependency arrays. Instead we memo the merged data to make it a stable value
   *
   * @returns {TInstance | undefined} - The merged data or undefined if the main query is not successful.
   */
  const mergedData = useMemo(() => {
    const newData = structuredClone(mainQuery.data);

    if (newData && mainQuery.isSuccess && nestedQueries.isSuccess) {
      for (const value of nestedQueries.data) {
        const { data, fieldName, type } = value;
        const isManyRelation = type === 'manyrelation';
        const isRelation = type === 'relation';

        if (isManyRelation) {
          const newIds = new Set(data.map((d) => d.id));
          const alreadyExists = mainQuery.data[fieldName] as TInstance[];
          const removeDuplicates = alreadyExists.filter(
            (item) => !newIds.has(item.id)
          );
          newData[fieldName] = [
            ...removeDuplicates,
            ...data,
          ] as TInstance[keyof TInstance];
        }
        if (isRelation && data.at(0)) {
          newData[fieldName] = data[0] as TInstance[keyof TInstance];
        }
      }
    }

    return newData;
  }, [
    mainQuery.data,
    mainQuery.isSuccess,
    nestedQueries.data,
    nestedQueries.isSuccess,
  ]);

  /**
   * Returns the combined query result including merged data and loading/error states.
   */
  const isLoading = waitForAllNested
    ? mainQuery.isLoading || nestedQueries.isLoading
    : mainQuery.isLoading;
  const isFetching = waitForAllNested
    ? mainQuery.isFetching || nestedQueries.isFetching
    : mainQuery.isFetching;
  const isPending = waitForAllNested
    ? mainQuery.isPending || nestedQueries.isPending
    : mainQuery.isPending;
  const isRefetching = waitForAllNested
    ? mainQuery.isRefetching || nestedQueries.isRefetching
    : mainQuery.isRefetching;
  return {
    data: mergedData,
    isLoading,
    isFetching: isFetching,
    isPending: isPending,
    isError: mainQuery.isError,
    error: mainQuery.error,
    isSuccess: mainQuery.isSuccess,
    refetch: mainQuery.refetch,
    isRefetching: isRefetching,
    isLoadingError: mainQuery.isLoadingError,
    status: mainQuery.status,
  };
};
